import { useMemo } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { BRAND, CHART_SERIES } from '@/lib/brand';
import { evaluateChart, type ChartResult } from '@/features/intelligence/dashboard/evaluate';
import { formatAxis, formatBucket, formatUnit } from '@/features/intelligence/dashboard/series';
import type { ChartWidget } from '@/features/intelligence/dashboard/types';
import { widgetSourceIds } from '@/features/intelligence/dashboard/catalog';
import { useDashboard, useSourceState } from '../DashboardContext';
import { ChartTooltipContent, LegendRow, WidgetError, WidgetMessage, WidgetSkeleton, type TooltipSeriesInfo } from '../shared';

export function ChartWidgetView({ widget }: { widget: ChartWidget }) {
  const { bag, catalog, window, isMobile } = useDashboard();
  const sourceIds = widgetSourceIds(widget);
  const { loading, error } = useSourceState(sourceIds);
  const result = useMemo(() => evaluateChart(widget, bag, window, catalog), [widget, bag, window, catalog]);

  if (error) return <WidgetError message={error} />;
  if (result.unavailable) return <WidgetMessage icon={BarChart3} title="Nothing to chart" detail={result.unavailable} />;
  if (loading || !result.ready) return <WidgetSkeleton variant="chart" />;
  if (result.isEmpty) {
    return <WidgetMessage icon={BarChart3} title="No data in this window" detail="Try a longer range, or check the integration feeding this metric." />;
  }

  if (result.mode === 'breakdown') {
    return widget.chartType === 'donut' ? (
      <DonutChart result={result} showLegend={widget.showLegend} compact={widget.h <= 2 || widget.w <= 3} />
    ) : (
      <RankedBars result={result} />
    );
  }
  return <SeriesChart widget={widget} result={result} dense={isMobile || widget.w <= 4} />;
}

// ---------------------------------------------------------------------------
// Time series
// ---------------------------------------------------------------------------

function SeriesChart({ widget, result, dense }: { widget: ChartWidget; result: ChartResult; dense: boolean }) {
  const infoByKey: Record<string, TooltipSeriesInfo> = {};
  for (const s of result.series) {
    infoByKey[s.key] = { label: s.label, unit: s.unit, color: s.color };
    if (s.previousKey) infoByKey[s.previousKey] = { label: `${s.label} (prior)`, unit: s.unit, color: s.color };
  }
  const ordered = [
    ...result.series.filter((s) => s.kind === 'bar'),
    ...result.series.filter((s) => s.kind === 'area'),
    ...result.series.filter((s) => s.kind === 'line'),
  ];
  const usesRight = result.unitRight != null;
  const legend = widget.showLegend && (result.series.length > 1 || widget.compare);

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={result.rows} margin={{ top: 6, right: usesRight ? 0 : 8, left: 0, bottom: 0 }}>
            <defs>
              {result.series
                .filter((s) => s.kind === 'area')
                .map((s) => (
                  <linearGradient key={s.key} id={`fill-${widget.id}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
            </defs>
            <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
            <XAxis
              dataKey="t"
              stroke={BRAND.oldGrowth}
              fontSize={11}
              tickLine={false}
              minTickGap={dense ? 28 : 16}
              tickFormatter={(v: string) => formatBucket(v, result.grain)}
            />
            <YAxis
              yAxisId="left"
              stroke={BRAND.oldGrowth}
              fontSize={11}
              width={dense ? 40 : 52}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => formatAxis(v, result.unitLeft)}
            />
            {usesRight && (
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={BRAND.oldGrowth}
                fontSize={11}
                width={dense ? 40 : 52}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => formatAxis(v, result.unitRight ?? 'count')}
              />
            )}
            <Tooltip
              cursor={{ fill: BRAND.linen, fillOpacity: 0.5, stroke: BRAND.linenDark }}
              content={<ChartTooltipContent grain={result.grain} infoByKey={infoByKey} />}
            />
            {ordered.map((s) => {
              if (s.kind === 'bar') {
                return (
                  <Bar
                    key={s.key}
                    yAxisId={s.axis}
                    dataKey={s.key}
                    fill={s.color}
                    stackId={s.stackId}
                    radius={s.stackId ? [0, 0, 0, 0] : [3, 3, 0, 0]}
                    maxBarSize={28}
                    isAnimationActive={false}
                  />
                );
              }
              if (s.kind === 'area') {
                return (
                  <Area
                    key={s.key}
                    yAxisId={s.axis}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={2}
                    fill={`url(#fill-${widget.id}-${s.key})`}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                );
              }
              return (
                <Line
                  key={s.key}
                  yAxisId={s.axis}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
            {result.series
              .filter((s) => s.previousKey)
              .map((s) => (
                <Line
                  key={s.previousKey}
                  yAxisId={s.axis}
                  type="monotone"
                  dataKey={s.previousKey!}
                  stroke={s.color}
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {legend && (
        <LegendRow
          className="pt-2"
          items={[
            ...result.series.map((s) => ({ label: s.label, color: s.color })),
            ...(widget.compare ? [{ label: 'Prior period', color: BRAND.oldGrowth, dashed: true }] : []),
          ]}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdowns
// ---------------------------------------------------------------------------

function DonutChart({ result, showLegend, compact }: { result: ChartResult; showLegend: boolean; compact: boolean }) {
  const total = result.slices.reduce((s, x) => s + x.value, 0);
  const infoByKey: Record<string, TooltipSeriesInfo> = {};
  result.slices.forEach((s, i) => {
    infoByKey[s.label] = { label: s.label, unit: result.sliceUnit, color: CHART_SERIES[i % CHART_SERIES.length] };
  });
  return (
    <div className={compact ? 'flex h-full items-center gap-3' : 'flex h-full flex-col'}>
      <div className="relative min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={result.slices}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={result.slices.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {result.slices.map((s, i) => (
                <Cell key={s.label} fill={CHART_SERIES[i % CHART_SERIES.length]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltipContent infoByKey={infoByKey} labelFormatter={() => ''} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-medium tabular-nums text-foreground">{formatUnit(total, result.sliceUnit, { compact: true })}</span>
          {!compact && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">total</span>}
        </div>
      </div>
      {showLegend && (
        <ul className={compact ? 'w-1/2 space-y-1 text-[11px]' : 'mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]'}>
          {result.slices.slice(0, compact ? 4 : 8).map((s, i) => (
            <li key={s.label} className="flex min-w-0 items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: CHART_SERIES[i % CHART_SERIES.length] }} />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="shrink-0 tabular-nums text-foreground">{total ? `${Math.round((s.value / total) * 100)}%` : '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RankedBars({ result }: { result: ChartResult }) {
  const infoByKey: Record<string, TooltipSeriesInfo> = { value: { label: 'Value', unit: result.sliceUnit, color: BRAND.oldGrowth } };
  const rows = result.slices.map((s) => ({ ...s, short: s.label.length > 22 ? `${s.label.slice(0, 21)}…` : s.label }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barCategoryGap={4}>
        <CartesianGrid stroke={BRAND.linenDark} horizontal={false} />
        <XAxis
          type="number"
          stroke={BRAND.oldGrowth}
          fontSize={11}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatAxis(v, result.sliceUnit)}
        />
        <YAxis type="category" dataKey="short" width={110} stroke={BRAND.oldGrowth} fontSize={11} tickLine={false} axisLine={false} />
        <Tooltip
          cursor={{ fill: BRAND.linen, fillOpacity: 0.5 }}
          content={<ChartTooltipContent infoByKey={infoByKey} labelFormatter={(l) => rows.find((r) => r.short === l)?.label ?? l} />}
        />
        <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={22} isAnimationActive={false}>
          {rows.map((s, i) => (
            <Cell key={s.label} fill={s.label === 'Other' ? BRAND.linenDark : CHART_SERIES[i % CHART_SERIES.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
